import type { ReactNode } from 'react';
import type { EventRegistrationDto, PaymentMethodDto } from '@/services/platform/api/model';
import type { CardField } from '@/components/shared/payment/card-fields';
import type { MewsBookingState } from '@/services/platform/data/booking/mews/types';

/** Which card fields are complete, and which have been typed into at all. */
export interface CardFieldState {
  complete: Record<CardField, boolean>;
  /**
   * Tracked separately from `complete`: a half-typed number is neither complete nor invalid, but it
   * is still entered data, so the screen has to treat the row as engaged.
   */
  touched: Record<CardField, boolean>;
}

/**
 * Everything the customer has typed or ticked.
 *
 * Owned by `EventCheckoutScreen` rather than by the form, because selecting MB WAY re-keys the
 * `<Elements>` group — the deferred-mode options differ from the card ones and the two cannot share
 * a group — which unmounts `CheckoutForm`. Holding this state inside it meant switching method wiped
 * the card, the name and the accepted terms.
 */
export interface CheckoutFormState {
  termsAccepted: boolean;
  marketingOptIn: boolean;
  holderName: string;
  saveForFuture: boolean;
  cardFields: CardFieldState;
  /** Per-field messages from Stripe. */
  cardErrors: Partial<Record<CardField, string>>;
  /** Set after a blocked pay attempt, so untouched required fields can be flagged. */
  submitAttempted: boolean;
}

export const EMPTY_CHECKOUT_FORM_STATE: CheckoutFormState = {
  termsAccepted: false,
  marketingOptIn: false,
  holderName: '',
  saveForFuture: false,
  cardFields: {
    complete: { number: false, expiry: false, cvc: false },
    touched: { number: false, expiry: false, cvc: false },
  },
  cardErrors: {},
  submitAttempted: false,
};

/**
 * Which option in the payment list is selected.
 *
 * Not the same thing as the API's `paymentMethodType`: `savedCard` and `wallet` both send `card`
 * (Apple Pay and Google Pay are card payment methods to Stripe), and only the collection mechanism
 * differs. `saved:<pm_id>` carries the card id so multiple saved cards each get their own row.
 */
export type CheckoutSelection =
  | { kind: 'savedCard'; paymentMethodId: string }
  | { kind: 'newCard' }
  | { kind: 'wallet' }
  | { kind: 'mbWay' };

/** Stable row key for the radio group — a saved card's key includes its Stripe id. */
export function selectionKey(selection: CheckoutSelection): string {
  return selection.kind === 'savedCard' ? `saved:${selection.paymentMethodId}` : selection.kind;
}

/**
 * What the checkout needs to know about the event being paid for.
 *
 * Travels from the book flow as router state rather than being re-fetched: the customer already
 * chose an occurrence, a venue and a guest count in step 0, and re-deriving that here would risk
 * charging for a different occurrence than the one they picked. `eventId` is the only field the
 * server needs (`contextReference`); the rest is for the summary panel and the price line.
 */
export interface EventCheckoutState {
  eventId: string;
  occurrenceId?: string;
  numberOfGuests?: number;
  /** Venue name for the WHERE row. */
  venueName?: string;
  /** ISO timestamp of the occurrence, for the WHEN row. */
  occurrenceStart?: string;
  /** Per-guest price in major units, as resolved by `resolveBookingScope`. */
  unitPrice?: number;
  currencyCode?: string;
  /** Where to return on back / after payment — the book flow route this came from. */
  returnPath: string;
}

/** Router state handed back to the book flow once the server has registered the paid booking. */
export interface PaidRegistrationState {
  eventId: string;
  occurrenceId?: string;
  numberOfGuests?: number;
  paidRegistration: EventRegistrationDto;
}

/**
 * Router state handed back to the book flow when the checkout refused the booking before charging,
 * in a way the member can fix there: `seatsLeft` — fewer seats than the party (409
 * `event_sold_out`), so "How many" is re-asked on the same occurrence; `dateGone` — the occurrence
 * no longer exists in Salesforce (404 `occurrence_not_found`), so the date is dropped.
 */
export interface EventCheckoutRetryState {
  eventId: string;
  occurrenceId?: string;
  seatsLeft?: number;
  dateGone?: boolean;
}

/**
 * What `CheckoutForm`/`CheckoutScreen` need to know about any purchase, regardless of context.
 *
 * The mirror image of `EventCheckoutState`: that one is Event-shaped (occurrence, per-guest
 * price); this is what's left once a context's own booking-specific fields are stripped down to
 * what the generic Stripe/polling machinery actually touches. `eventBooking` is the one
 * context-specific extra payload that survives here — Mews (and every other non-Event context)
 * never sets it, since its reservation already exists and only needs `contextReference`
 * (`CreateCheckoutPaymentIntentRequest`'s own doc comment: "Every other flow ... already has a
 * row and only needs its ContextReference").
 */
export interface CheckoutPurchase {
  context: string;
  contextReference: string;
  amountMajor: number;
  currencyCode?: string;
  returnPath: string;
  eventBooking?: { occurrenceId?: string | null; numberOfGuests?: number };
  /** Rows for the "view booking summary" accordion — built by each adapter from its own state. */
  summaryRows?: { label: string; value: string; hint?: string }[];
  /** Accordion trigger label. Defaults to `App.Checkout.ViewBookingSummary` when unset. */
  summaryTitle?: string;
  /**
   * Rendered in place of `BookingSummaryPanel` when a context has a richer summary than label/value
   * pairs — the shop shows its actual line items, reusing the same panel the bag and delivery
   * screens draw, so the basket looks the same on the screen that charges for it.
   *
   * Safe to hold a node here: a `CheckoutPurchase` is rebuilt in each adapter's `useMemo` and is
   * never the thing persisted to `sessionStorage` (that is the adapter's own state shape).
   */
  summaryContent?: ReactNode;
}

/**
 * What the Mews-backed flows (sunbed, stay, the Mews branch of experience) need to know about the
 * reservation being paid for.
 *
 * Unlike events, the reservation already exists (`AwaitingPayment`, held) by the time checkout is
 * reached — `reservationGuid` is both the context reference and the thing the confirmation screen
 * re-fetches afterward. `amountMajor` travels because `ReservationDetailDto` carries no amount
 * field; it is the same total already shown to the guest during booking (see
 * `flows/shared/mews/details.tsx`'s `grandTotal`), which the server independently re-resolves and never
 * trusts from the client.
 */
export interface MewsCheckoutState {
  reservationGuid: string;
  amountMajor: number;
  currencyCode?: string;
  returnPath: string;
  /**
   * The flow's full local booking state, serialized as router state. `dateFrom`/`dateTo` survive
   * a JSON round-trip as ISO strings, not `Date` instances — the receiving flow must revive them.
   */
  booking: MewsBookingState;
}

/**
 * Router state handed back to a Mews-backed flow once the reservation is paid for.
 *
 * Unlike `PaidRegistrationState`, a bare id is not enough to rebuild the confirmation screen:
 * `SunbedConfirmation`/`StayConfirmation`/etc. render from the flow's own `booking` (allocations,
 * extras, venue image/logo, `Date` objects) as much as from the reservation itself, so the whole
 * `booking` travels back rather than being re-derived from a `getReservation` refetch alone.
 */
export interface MewsPaidState {
  reservationGuid: string;
  booking: MewsBookingState;
}

/** A row in the payment list, after CMS content and server availability have been reconciled. */
export interface CheckoutMethodOption {
  selection: CheckoutSelection;
  label: string;
  /** Logos rendered on the right of the row. */
  logos: MethodLogo[];
  /** Present only for `savedCard`, to render the brand and last four. */
  savedCard?: PaymentMethodDto;
}

export interface MethodLogo {
  src: string;
  alt: string;
  /** Intrinsic width in px from the design — logos are never stretched to fill their tile. */
  width: number;
  height: number;
  /** Tile background. Amex is the only brand with a coloured tile. */
  background?: string;
}
